/**
 * @file Reads the lang attribute from the html object to detect the language.
 */
const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
const currentCountryIso31662 = document.documentElement.getAttribute('data-country') ?? 'XX'

// The current long-form time zone name, eg "Singapore Standard Time". If not supported, returns tz (IANA)
// https://stackoverflow.com/a/67363188
const timeZoneName =
  new Intl.DateTimeFormat([], { timeZoneName: 'long' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value ?? tz

// we have functions to get the current locale so that we can get it when the function is called
// when full client surface is on this lets us get latest value as it may be stale
const withDefaults = (locale: string | undefined): string => {
  if (locale == null || locale === 'auto') return 'en'
  return locale
}
const getHyphenatedCurrentLocale = (): string => withDefaults(document.documentElement.lang || 'en')
const getUnnormalizedHyphenatedCurrentLocale = (): string =>
  withDefaults(document.documentElement.dataset.lang || document.documentElement.lang || 'en')

const getLang = (): string => getHyphenatedCurrentLocale().split('-')[0]

const getUnderscoredCurrentLocale = (): string => getHyphenatedCurrentLocale().replace(/-/g, '_')

/**  https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes
31661 (alpha 2) is the 2-letter code for a country (e.g. US)
31662 is the 2-letter code for a country and subregion (e.g. US-CA)
*/
const getCurrentCountryIso31661 = (): string =>
  document.documentElement.getAttribute('data-country')?.slice(0, 2) ?? 'XX'
const getCurrentCountryIso31662 = (): string => document.documentElement.getAttribute('data-country') ?? 'XX'

/**
 * List of timezones for countries
 * that follow GDPR as of 19/03/25.
 * @see https://www.iana.org/time-zones
 * @see Intl::Country#follows_gdpr?
 */
const gdprTimezones = [
  // Inside EU
  'Europe/Vienna',
  'Europe/Brussels',
  'Europe/Sofia',
  'Europe/Zagreb',
  'Asia/Nicosia',
  'Europe/Prague',
  'Europe/Copenhagen',
  'Europe/Tallinn',
  'Europe/Helsinki',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Athens',
  'Europe/Budapest',
  'Europe/Dublin',
  'Europe/Rome',
  'Europe/Riga',
  'Europe/Vilnius',
  'Europe/Luxembourg',
  'Europe/Malta',
  'Europe/Amsterdam',
  'Europe/Warsaw',
  'Europe/Lisbon',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  'Europe/Bucharest',
  'Europe/Bratislava',
  'Europe/Ljubljana',
  'Europe/Madrid',
  'Atlantic/Canary',
  'Europe/Stockholm',
  // Outside EU
  'Europe/London',
  'Europe/Zurich',
  'Europe/Oslo',
  'Atlantic/Reykjavik',
  'Europe/Vaduz',
]
const followsGdpr = gdprTimezones.includes(tz)

export {
  currentCountryIso31662,
  followsGdpr,
  getCurrentCountryIso31661,
  getCurrentCountryIso31662,
  getHyphenatedCurrentLocale,
  getLang,
  getUnderscoredCurrentLocale,
  getUnnormalizedHyphenatedCurrentLocale,
  timeZoneName,
  tz,
}
